import { useState } from "react";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Film, ImageIcon, Loader2, Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const isVideo = (m) => (m.content_type || "").startsWith("video/");

function Thumb({ url, video }) {
  return video ? (
    <video src={url} className="h-full w-full object-cover" muted preload="metadata" />
  ) : (
    <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
  );
}

function LibraryPicker({ open, onClose, media, usedIds, onPick }) {
  const list = media.filter((m) => !usedIds.includes(m.id) && (m.content_type || "").match(/^(image|video)\//));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="wall-library-dialog">
        <DialogHeader>
          <DialogTitle>Elegir contenido para el Video Wall</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Se cortará automáticamente en pedazos, uno por cada TV. Usa imágenes o videos horizontales grandes para mejor calidad.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {list.length === 0 && <p className="col-span-full text-sm text-slate-500">No hay contenido disponible. Súbelo primero en la Biblioteca.</p>}
          {list.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onPick(m)}
              className="group text-left rounded-lg border border-slate-200 overflow-hidden hover:border-blue-500 hover:shadow-md transition-[border-color,box-shadow] duration-200"
              data-testid={`wall-library-item-${m.id}`}
            >
              <div className="aspect-video bg-slate-100"><Thumb url={m.storage_path} video={isVideo(m)} /></div>
              <div className="px-2 py-1.5 flex items-center gap-1.5 text-[11px] text-slate-700">
                {isVideo(m) ? <Film className="h-3 w-3 shrink-0" /> : <ImageIcon className="h-3 w-3 shrink-0" />}
                <span className="truncate">{m.filename}</span>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  if (status === "ready") return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700"><CheckCircle2 className="h-3 w-3" />Listo</span>;
  if (status === "failed") return <span className="inline-flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle className="h-3 w-3" />Error</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><Loader2 className="h-3 w-3 animate-spin" />Cortando…</span>;
}

export default function WallContent({ wall, media, onChanged, previewId, onPreview, mode }) {
  const [picking, setPicking] = useState(false);
  const items = wall.items || [];

  const add = async (m) => {
    setPicking(false);
    try {
      const { data } = await apiAdmin.post(`/video-walls/${wall.id}/items`, { media_id: m.id });
      onChanged(data);
      toast.success("Contenido agregado, cortándolo para cada TV…");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (mid) => {
    try {
      const { data } = await apiAdmin.delete(`/video-walls/${wall.id}/items/${mid}`);
      onChanged(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="wall-content">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-heading text-base font-semibold text-slate-900">Contenido gigante</div>
          <p className="text-xs text-slate-500">
            {mode === "playlist" ? "Si agregas varios, salen uno tras otro al final de cada vuelta." : "Si agregas varios, se turnan en cada aparición."}
          </p>
        </div>
        <Button size="sm" onClick={() => setPicking(true)} data-testid="wall-add-content-btn">
          <Plus className="h-4 w-4 mr-1" />Agregar
        </Button>
      </div>
      {items.length === 0 && (
        <p className="text-sm text-slate-400 py-4" data-testid="wall-content-empty">Todavía no hay contenido para este grupo.</p>
      )}
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.media_id}
            onClick={() => onPreview(it.media_id)}
            className={`flex items-center gap-3 rounded-lg border p-2 cursor-pointer transition-colors duration-200 ${
              previewId === it.media_id ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`wall-item-${it.media_id}`}
          >
            <div className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-100">
              <Thumb url={it.preview_url} video={isVideo(it)} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-slate-800">{it.filename}</div>
              <StatusBadge status={it.status} />
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(it.media_id); }}
              className="rounded-md p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              aria-label="Quitar contenido"
              data-testid={`wall-item-remove-${it.media_id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <LibraryPicker open={picking} onClose={() => setPicking(false)} media={media} usedIds={items.map((i) => i.media_id)} onPick={add} />
    </div>
  );
}
